/**
 * VerificationStatus Component
 * Displays verification status with appropriate icons and messages
 */

import React from 'react'

interface VerificationStatusProps {
  status: 'loading' | 'success' | 'error'
  errorMessage?: string
  onRetry?: () => void
  children?: React.ReactNode
}

export function VerificationStatus({
  status,
  errorMessage,
  onRetry,
  children,
}: VerificationStatusProps) {
  if (status === 'loading') {
    return (
      <div style={styles.loadingContainer} role="status" aria-live="polite">
        <div style={styles.spinner} aria-hidden="true"></div>
        <p style={styles.loadingText}>正在驗證您的 Email...</p>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div style={styles.successContainer} role="alert" aria-live="polite">
        <div style={styles.successIconWrapper} aria-hidden="true">
          <svg
            style={styles.icon}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 style={styles.successTitle}>Email 驗證成功!</h2>
        <p style={styles.successText}>
          您的 Email 已成功驗證,即將跳轉至 Dashboard...
        </p>
        {children}
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div style={styles.errorContainer} role="alert" aria-live="polite">
        <div style={styles.errorIconWrapper} aria-hidden="true">
          <svg
            style={styles.icon}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <h2 style={styles.errorTitle}>驗證失敗</h2>
        <p style={styles.errorText}>
          {errorMessage || '驗證連結可能已過期或無效'}
        </p>
        {children}
        {onRetry && (
          <button onClick={onRetry} style={styles.retryButton}>
            重試
          </button>
        )}
      </div>
    )
  }

  return null
}

const styles: Record<string, React.CSSProperties> = {
  loadingContainer: {
    textAlign: 'center',
    padding: '48px 0',
  },
  spinner: {
    display: 'inline-block',
    width: '64px',
    height: '64px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #2563eb',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  loadingText: {
    fontSize: '18px',
    color: '#374151',
  },
  successContainer: {
    textAlign: 'center',
    padding: '32px 0',
  },
  successIconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    backgroundColor: '#dcfce7',
    borderRadius: '50%',
    marginBottom: '16px',
  },
  icon: {
    width: '32px',
    height: '32px',
    color: '#16a34a',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '8px',
  },
  successText: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '24px',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '32px 0',
  },
  errorIconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    backgroundColor: '#fee2e2',
    borderRadius: '50%',
    marginBottom: '16px',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '8px',
  },
  errorText: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '24px',
  },
  retryButton: {
    marginTop: '16px',
    padding: '8px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
}

// Add keyframes animation via style tag
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `
  if (!document.head.querySelector('style[data-spin-animation]')) {
    styleSheet.setAttribute('data-spin-animation', 'true')
    document.head.appendChild(styleSheet)
  }
}
